<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import Usuario from '@/models/Usuario';
import InputFechaHora from '../cifra/InputFechaHora.vue';
import itemsDiariosApi from '../../api/cifra/items_diarios';
import TablaItemsDiarios from './TablaItemsDiarios.vue';
import ModalEstadisticas from './ModalEstadisticas.vue';

const props = defineProps({
  alumno: { type: Object, default: null },
  fecha: { type: String, default: '' },
});

const fechaSeleccionada = ref(props.fecha);
const itemsDiarios = ref([]);
const fechasConItemsDiarios = ref([]);
const fechasConItemsDiariosVistos = ref([]);
const cargando = ref(false);

// Computadas para el manejo de items (movidas desde ItemsDiariosTabla)
const itemsSinAsignatura = computed(() => {
  const items = itemsDiarios.value;
  return items.filter((item) => !item.asignatura?.id);
});
const itemsConAsignatura = computed(() => itemsDiarios.value.filter((item) => item.asignatura?.id));

const itemsAgrupadosPorAsignatura = computed(() => {
  const grupos = {};
  itemsConAsignatura.value.forEach((item) => {
    // Obtener el nombre de la asignatura desde la estructura correcta
    const nombreAsignatura = item.asignatura?.nombre || 'Sin asignatura';

    if (!grupos[nombreAsignatura]) {
      grupos[nombreAsignatura] = {
        nombre: nombreAsignatura,
        asignatura: item.asignatura,
        items: [],
      };
    }
    grupos[nombreAsignatura].items.push(item);
  });
  return grupos;
});

const hayItems = computed(() => itemsDiarios.value.length > 0);

const fechasEstilizadas = computed(() => {
  const fechasConItems = fechasConItemsDiarios.value.reduce((acc, f) => {
    acc[f] = 'btn-success';
    return acc;
  }, {});

  const fechasVistas = fechasConItemsDiariosVistos.value.reduce((acc, f) => {
    acc[f] = 'btn-default';
    return acc;
  }, {});

  return { ...fechasConItems, ...fechasVistas };
});

async function cargarInfoGeneralAlumno() {
  if (!props.alumno?.id) {
    fechasConItemsDiarios.value = [];
    fechasConItemsDiariosVistos.value = [];
    return;
  }

  try {
    const response = await itemsDiariosApi.infoGeneralAlumno(props.alumno.id);
    const data = response.data || {};

    fechasConItemsDiarios.value = data.fechas_con_items_diarios || [];
    fechasConItemsDiariosVistos.value = data.fechas_con_items_diarios_vistos || [];
  } catch (error) {
    // Error cargando información general del alumno
    fechasConItemsDiarios.value = [];
    fechasConItemsDiariosVistos.value = [];
  }
}

async function cargarItemsDiarios() {
  if (!props.alumno?.id || !fechaSeleccionada.value) {
    itemsDiarios.value = [];
    return;
  }

  try {
    cargando.value = true;
    const response = await itemsDiariosApi.calificacionesAlumno(
      props.alumno.id,
      fechaSeleccionada.value,
    );

    itemsDiarios.value = response.data || [];

    // Recargar información general del alumno para actualizar fechas vistas
    await cargarInfoGeneralAlumno();
  } catch (error) {
    // Error cargando items diarios
    itemsDiarios.value = [];
  } finally {
    cargando.value = false;
  }
}

async function onCambioFecha(nuevaFecha) {
  fechaSeleccionada.value = nuevaFecha;
}

// Watcher para recargar cuando cambien el alumno o la fecha inicial
watch(
  () => [props.alumno?.id, props.fecha],
  async (newValues, oldValues) => {
    const [newAlumnoId, newFecha] = newValues;
    const [oldAlumnoId] = oldValues || [null, null];

    fechaSeleccionada.value = newFecha;

    // Si cambió el alumno, recargar info general
    if (newAlumnoId !== oldAlumnoId) {
      await cargarInfoGeneralAlumno();
    }

    // Siempre recargar items cuando cambien alumno o fecha
    cargarItemsDiarios();
  },
  { immediate: true },
);

// Watcher para recargar cuando cambie la fecha seleccionada localmente
// (sin incluir cambios por props.fecha que ya maneja el watcher anterior)
watch(
  fechaSeleccionada,
  (newFecha, oldFecha) => {
    if (newFecha !== oldFecha) {
      cargarItemsDiarios();
    }
  },
);

/* Tema de estadísticas */
const showModal = ref(false);
const asignaturaSeleccionada = ref(null);

function mostrarEstadisticas(asignatura) {
  asignaturaSeleccionada.value = asignatura;
  showModal.value = true;
}
/* Fin tema de estadísticas */
</script>

<template>
  <div id="pagina-diarios-wrapper">
    <div class="m-t">
      <InputFechaHora
        :model-value="fechaSeleccionada"
        :styled-dates="fechasEstilizadas"
        name="fecha"
        @update:model-value="onCambioFecha"
      />
    </div>

    <spinner v-if="cargando" :label="true" />

    <!-- Contenido de items diarios integrado -->
    <div v-else-if="hayItems">
      <!-- Items sin asignatura -->
      <div v-if="itemsSinAsignatura.length > 0">
        <div class="card">
          <div class="card-header border-bottom-cifra">
            {{ $t('Diario') }}
            <a
              class="btn btn-cifra pull-right"
              @click="mostrarEstadisticas(null)">
              <i class="fa fa-chart-bar"></i> {{ $t('Estadisticas') }}
            </a>
          </div>
          <div class="card-body">
            <TablaItemsDiarios :items="itemsSinAsignatura" />
          </div>
        </div>
      </div>

      <!-- Items agrupados por asignatura -->
      <div
        v-for="(dataAsignatura, asignatura) in itemsAgrupadosPorAsignatura"
        :key="asignatura"
        class="card m-t"
      >
        <div class="card-header border-bottom-cifra">
          {{ asignatura }}
          <a
            class="btn btn-cifra pull-right"
            @click="mostrarEstadisticas(dataAsignatura.asignatura)">
            <i class="fa fa-chart-bar"></i> {{ $t('Estadisticas') }}
          </a>
        </div>
        <div class="card-body">
          <TablaItemsDiarios :items="dataAsignatura.items" />
        </div>
      </div>
    </div>

    <!-- No hay registros -->
    <div v-else class="no-registros text-center m-t">
      <i class="fal fa-book"></i>
      <h3 class="m-t-lg">
        {{ $t('Calificaciones_no_hay') }}
      </h3>
    </div>
    <ModalEstadisticas
      v-model="showModal"
      :alumno="new Usuario(alumno)"
      :asignatura="asignaturaSeleccionada"
      :fecha="fechaSeleccionada"
      :evaluacion="null"
    />
  </div>
</template>

<style lang="scss" scoped>
.no-registros {
  i {
    font-size: 198px;
    opacity: 50%
  }
}
</style>
